import React, { useState } from "react";
import Nav from "react-bootstrap/Nav";
import Button from "react-bootstrap/Button";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BarChart3,
  Calendar,
  Shirt,
  User,
  Users,
  UserCheck,
  X,
  Headset,
  PackageCheck,
  ChevronDown,
  ChevronRight,
  History,
  Monitor,
} from "lucide-react";
import { SidebarProps } from "../../types";
import "./Sidebar.css";

interface ExtendedSidebarProps extends SidebarProps {
  collapsed?: boolean;
  isMobile?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<ExtendedSidebarProps> = ({
  collapsed = false,
  isMobile = false,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [clientesMenuOpen, setClientesMenuOpen] = useState(false);

  const menuItems = [
    { path: "/", label: "Dashboard", icon: <BarChart3 size={20} /> },
    {
      path: "/clientes",
      label: "Clientes",
      icon: <Users size={20} />,
      submenu: [
        {
          path: "/clientes/historico",
          label: "Histórico de Clientes",
          icon: <History size={18} />,
        },
      ],
    },
    { path: "/produtos", label: "Produtos", icon: <Shirt size={20} /> },
    { path: "/reservas", label: "Reservas", icon: <Calendar size={20} /> },
    {
      path: "/devolucoes",
      label: "Devoluções",
      icon: <PackageCheck size={20} />,
    },
    { path: "/atendimento", label: "Atendimento", icon: <Headset size={20} /> },
    {
      path: "/funcionarios",
      label: "Funcionários",
      icon: <UserCheck size={20} />,
    },
    { path: "/painel", label: "Painel", icon: <Monitor size={20} /> },
    { path: "/perfil", label: "Perfil", icon: <User size={20} /> },
  ];

  return (
    <div
      className={`sidebar text-white d-flex flex-column ${collapsed ? "sidebar-collapsed" : ""}`}
      style={{
        width: isMobile ? "280px" : "250px",
        background: "linear-gradient(135deg, #162f5f 0%, #1f427f 100%)",
        position: "fixed",
        left: isMobile ? (collapsed ? "-280px" : "0") : "0",
        top: 0,
        zIndex: isMobile ? 1000 : 999,
        transition: "left 0.3s ease",
        height: "100vh",
        maxHeight: "100vh",
      }}
    >
      {/* Efeito de overlay similar ao login */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: `
            radial-gradient(circle at 20% 80%, rgba(74, 111, 165, 0.4) 0%, transparent 60%),
            radial-gradient(circle at 80% 20%, rgba(45, 74, 115, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(26, 42, 74, 0.2) 0%, transparent 40%),
            radial-gradient(circle at 60% 60%, rgba(10, 22, 40, 0.3) 0%, transparent 50%)
          `,
          zIndex: 1,
        }}
      />

      {/* Cabeçalho - altura fixa */}
      <div
        className="p-4"
        style={{ position: "relative", zIndex: 2, flexShrink: 0 }}
      >
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h4 className="text-center mb-0 d-flex align-items-center justify-content-center gap-2 flex-grow-1">
            <span className="text-white fw-bold">
              System
              <span className="text-white-50">Loc</span>
            </span>
          </h4>
          {/* Botão de fechar para mobile */}
          {isMobile && onClose && (
            <Button
              variant="outline-light"
              onClick={onClose}
              className="d-flex align-items-center justify-content-center ms-2"
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                background: "rgba(255, 255, 255, 0.1)",
                padding: "0",
              }}
            >
              <X size={16} />
            </Button>
          )}
        </div>
      </div>

      {/* Menu rolável - com scroll independente */}
      <div
        className="flex-grow-1 px-3"
        style={{
          position: "relative",
          zIndex: 2,
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
          paddingBottom: "20px",
        }}
      >
        <Nav className="flex-column">
          {menuItems.map((item) => (
            <Nav.Item key={item.path}>
              <Nav.Link
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (item.submenu) {
                    // Toggle submenu
                    if (item.path === "/clientes") {
                      setClientesMenuOpen(!clientesMenuOpen);
                      // Navega para a página principal ao abrir o submenu
                      if (!clientesMenuOpen) {
                        navigate(item.path);
                      }
                    }
                  } else {
                    navigate(item.path);
                  }
                  // Fechar sidebar no mobile após navegar
                  if (isMobile && onClose && !item.submenu) {
                    onClose();
                  }
                }}
                className={`text-white mb-2 d-flex align-items-center justify-content-between gap-3 px-3 py-3 ${
                  location.pathname === item.path
                    ? "bg-primary bg-opacity-25"
                    : "hover-bg-secondary hover-bg-opacity-25"
                }`}
                style={{
                  borderRadius: "12px",
                  transition: "all 0.3s ease",
                  border:
                    location.pathname === item.path
                      ? "1px solid rgba(255, 255, 255, 0.2)"
                      : "none",
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <span>{item.icon}</span>
                  <span className="fw-semibold">{item.label}</span>
                </div>
                {item.submenu && (
                  <span>
                    {item.path === "/clientes" && clientesMenuOpen ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )}
                  </span>
                )}
              </Nav.Link>

              {/* Submenu */}
              {item.submenu &&
                item.path === "/clientes" &&
                clientesMenuOpen && (
                  <div className="ms-3 mt-1 mb-2">
                    {item.submenu.map((subItem) => (
                      <Nav.Link
                        key={subItem.path}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(subItem.path);
                          // Fechar sidebar no mobile após navegar
                          if (isMobile && onClose) {
                            onClose();
                          }
                        }}
                        className={`text-white-50 mb-1 d-flex align-items-center gap-2 px-3 py-2 ${
                          location.pathname === subItem.path
                            ? "bg-primary bg-opacity-15 text-white"
                            : "hover-bg-secondary hover-bg-opacity-10"
                        }`}
                        style={{
                          borderRadius: "8px",
                          transition: "all 0.3s ease",
                          fontSize: "0.9rem",
                        }}
                      >
                        <span>{subItem.icon}</span>
                        <span>{subItem.label}</span>
                      </Nav.Link>
                    ))}
                  </div>
                )}
            </Nav.Item>
          ))}
        </Nav>
      </div>
    </div>
  );
};

export default Sidebar;
